import { AnimatedSprite, Container, Graphics, InteractionEvent, Loader, Sprite, Texture } from 'pixi.js';
import { Game, intersectionLL, Scene, Store, TextureFactory } from '../core';
import { IEventEmitter, Line, Updatable } from '../interface';
import { Player } from '../class/running';
import { DrawingGameUI, VanishText } from '../ui';
import MainScene from './MainScene';
import RunningGameProgress from '../ui/RunningGameProgress';
import CucumberSpriteInfo from '../class/drawing/Cucumber.json';
import EggplantSpriteInfo from '../class/drawing/Eggplant.json';
import PumpkinSpriteInfo from '../class/drawing/Pumpkin.json';
import { ThirdCutScene } from '.';
import { secondsFormatting, secondsToScore } from '../util';
import { sound } from '@pixi/sound';
import { LOCALE_DATA } from '../constant';
import { logging, LOG_EVENT, LOG_SCENE } from '../api/log';
import { loadGoogleAdvertisement, showGoogleAdvertisement } from '../util/AD';

class StoneObstacle extends Container {
  private sprite: Sprite;
  constructor() {
    super();

    const texture = TextureFactory.instance.getSingleTexture('/textures/objects/etc/stone.png');

    this.sprite = new Sprite(texture);
    this.sprite.scale.set(0.7);
    this.sprite.anchor.set(0.5, 0.9);

    this.addChild(this.sprite);
  }
}

class MonsterObstacle extends Container {
  private animatedSprite: AnimatedSprite;
  private monsterTextures: Array<Array<Texture>> = [];

  constructor() {
    super();

    const cucumberTextures = TextureFactory.instance.load(CucumberSpriteInfo);
    const eggplantTextures = TextureFactory.instance.load(EggplantSpriteInfo);
    const pumkinTextures = TextureFactory.instance.load(PumpkinSpriteInfo);

    this.monsterTextures.push(cucumberTextures.slice(80, 95));
    this.monsterTextures.push(eggplantTextures.slice(80, 93));
    this.monsterTextures.push(pumkinTextures.slice(126, 141));

    this.animatedSprite = new AnimatedSprite(this.monsterTextures[0]);
    this.animatedSprite.animationSpeed = 0.5;
    this.animatedSprite.loop = true;
    this.animatedSprite.x += 8;
    this.animatedSprite.y += 13;
    this.animatedSprite.play();

    this.addChild(this.animatedSprite);
  }

  public change(): void {
    this.animatedSprite.textures = this.monsterTextures[0];
    this.animatedSprite.animationSpeed = 0.5;
    this.animatedSprite.play();
  }
}

class HeartItem extends Container {
  private sprite: Sprite;
  public used: boolean = false;

  constructor() {
    super();
    const texture = TextureFactory.instance.getSingleTexture('/textures/objects/etc/heart.png');

    this.sprite = new Sprite(texture);
    this.sprite.anchor.set(0.5);

    this.addChild(this.sprite);
  }

  public use(): void {
    this.used = true;
  }

  public update(elapsed: number): void {
    if (this.used && this.alpha >= 0.01) {
      this.scale.x += (3 - this.scale.x) * 0.1 * elapsed;
      this.scale.y = this.scale.x;
      this.alpha += -this.alpha * 0.1 * elapsed;
    } else if (this.used) {
      this.visible = false;
    }
  }
}

class BreadItem extends Container {
  private sprite: Sprite;
  public used: boolean = false;

  constructor() {
    super();
    this.sprite = new Sprite(TextureFactory.instance.getSingleTexture('/textures/objects/etc/bread.png'));
    this.sprite.anchor.set(0.5);

    this.addChild(this.sprite);
  }

  public use(): void {
    this.used = true;
  }

  public update(elapsed: number): void {
    if (this.used && this.alpha >= 0.01) {
      this.scale.x += (3 - this.scale.x) * 0.1 * elapsed;
      this.scale.y = this.scale.x;
      this.alpha += -this.alpha * 0.1 * elapsed;
    } else if (this.used) {
      this.visible = false;
    } else if (!this.used && this.alpha <= 0.99) {
      this.visible = true;
      this.scale.x += (1 - this.scale.x) * 0.1 * elapsed;
      this.scale.y = this.scale.x;
      this.alpha += (1 - this.alpha) * 0.1 * elapsed;
    } else if (!this.used) {
      this.alpha = 1;
    }
  }
}

class DashItem extends Container {
  private sprite: Sprite;
  public used: boolean = false;

  constructor() {
    super();
    const texture = TextureFactory.instance.getSingleTexture('/textures/objects/etc/dash.png');

    this.sprite = new Sprite(texture);
    this.sprite.anchor.set(0.5);

    this.addChild(this.sprite);
  }

  public use(): void {
    this.used = true;
  }

  public update(elapsed: number): void {
    if (this.used && this.alpha >= 0.01) {
      this.scale.x += (3 - this.scale.x) * 0.1 * elapsed;
      this.scale.y = this.scale.x;
      this.alpha += -this.alpha * 0.1 * elapsed;
    } else if (this.used) {
      this.visible = false;
      this.used = false;
    } else if (this.alpha !== 1) {
      this.alpha = 1;
      this.scale.x = 1;
      this.scale.y = 1;
      this.visible = true;
    }
  }
}

class RunningGameScene extends Scene {
  private mapContainer: Container;
  private maps: Array<{
    line: Line;
    range: number;
  }>;
  private roadPolygon: Graphics;
  private drawLinePosition: number;
  private grasses: Array<Sprite>;
  private fences: Array<Sprite>;
  private upperFarms: Array<Sprite>;
  private bottomFarms: Array<Sprite>;
  private isBgmPlaying: boolean = false;
  private ui: DrawingGameUI;
  private progress: RunningGameProgress;
  private goalPosition: number = 104000; // 104000
  private objects: Array<Updatable & IEventEmitter>;
  private player: Player;
  private stoneObstacle: StoneObstacle;
  private monsterObstacle: MonsterObstacle;
  private heartItem: HeartItem;
  private breadItem: BreadItem;
  private dashItem: DashItem;
  private getBread: boolean = false;
  private lastPosition: number = 0;
  private showNotificationDT: number = 150;
  private showTapTutorial: boolean = false;

  constructor(game: Game) {
    super(game);

    this.drawLinePosition = -Infinity;
    this.grasses = [];
    this.fences = [];
    this.upperFarms = [];
    this.bottomFarms = [];

    this.camera.positionChasingCoefficient = 1;
    this.camera.setOffset({ x: 300, y: 0 });
    this.camera.setBoundary({
      min: { x: 0, y: -Infinity },
      max: { x: this.goalPosition + 150, y: Infinity },
    });

    this.maps = [
      {
        line: [
          { x: 0, y: 0 },
          { x: 690, y: 0 },
        ],
        range: 750,
      },
      {
        line: [
          { x: 690, y: 0 },
          { x: 1280, y: 100 },
        ],
        range: 750,
      },
    ];
    this.mapContainer = new Container();
    this.roadPolygon = new Graphics();
    this.roadPolygon.zIndex = 1;
    this.mapContainer.sortableChildren = true;
    this.mapContainer.addChild(this.roadPolygon);

    this.mapContainer.zIndex = -Infinity;
    this.stage.addChild(this.mapContainer);

    this.player = new Player();
    this.player.position.x = 0;
    this.player.position.y = 0;
    this.player.targetPosition.x = this.player.position.x;
    this.player.targetPosition.y = this.player.position.y;
    this.player.velocity.x = 1;
    this.player.container.zIndex = 1;
    this.stage.addChild(this.player.container);

    this.player.on('DIE', (): void => {
      this.gameOver();
    });
    this.player.on('hit', (damage: number) => {
      const scoreText: VanishText = new VanishText(`HP -${damage}`, { fill: 0xff5555 });
      scoreText.x = this.player.position.x;
      scoreText.y = this.player.position.y - 90;
      scoreText.on('destroy', () => {
        this.objects.splice(this.objects.indexOf(scoreText), 1);
        this.stage.removeChild(scoreText);
        scoreText.destroy();
      });
      this.objects.push(scoreText);
      this.stage.addChild(scoreText);
    });

    this.player.on('firstDirectionChanged', () => {
      this.ui.showTitle('Start!');
    });

    this.camera.setTargetPosition(this.player.targetPosition, true);

    this.objects = [this.player];

    this.progress = new RunningGameProgress();
    this.container.addChild(this.progress);

    this.ui = new DrawingGameUI(this);
    this.ui.on('home', () => {
      const button = document.getElementById('sharebtn') as HTMLElement;
      button.style.display = 'none';
      this.game.changeScene(MainScene);
      logging(
        LOG_EVENT.GO_TO_HOME,
        LOG_SCENE.RUNNING_GAME_SCENE,
        Store.instance.data.playername,
        window.navigator.userAgent
      );
    });
    this.ui.on('pause', () => {
      this.game.stop();
    });
    this.ui.on('start', () => {
      this.game.start();
    });

    this.ui.on('ad', async () => {
      try {
        const isLoaded: boolean = await loadGoogleAdvertisement();
        if (isLoaded) {
          await showGoogleAdvertisement();
          logging(
            LOG_EVENT.AD,
            LOG_SCENE.JUMPING_GAME_SCENE,
            Store.instance.data.playername,
            window.navigator.userAgent
          );
        }
      } catch (e) {
        console.log(e);
      }
      if (Store.instance.state.hp < 3) {
        Store.instance.state.hp = 3;
      }
      const button = document.getElementById('sharebtn') as HTMLElement;
      button.style.display = 'none';
      this.game.changeScene(RunningGameScene);
      logging(
        LOG_EVENT.REPLAY,
        LOG_SCENE.RUNNING_GAME_SCENE,
        Store.instance.data.playername,
        window.navigator.userAgent
      );
    });
    this.container.addChild(this.ui);

    const background = new Graphics();
    background.beginFill(0xb1fb59);
    background.drawRect(0, 0, 1080, 1920);
    this.backgroundContainer.addChild(background);

    // Obstacles
    this.stoneObstacle = new StoneObstacle();
    this.stoneObstacle.x = -5000;
    this.stoneObstacle.y = -2000;
    this.stage.addChild(this.stoneObstacle);

    this.monsterObstacle = new MonsterObstacle();
    this.monsterObstacle.scale.set(1.2);
    this.monsterObstacle.x = -15000;
    this.monsterObstacle.y = -2000;
    this.stage.addChild(this.monsterObstacle);

    // Heart Item
    this.heartItem = new HeartItem();
    this.heartItem.x = -180000;
    this.heartItem.y = -4000;
    this.stage.addChild(this.heartItem);

    this.breadItem = new BreadItem();
    this.breadItem.x = Math.random() < 0.5 ? -190000 : Infinity;
    this.breadItem.y = -4000;
    if (Store.instance.data.costumes[2]) {
      this.breadItem.x = Infinity;
    }
    this.stage.addChild(this.breadItem);

    // Dash Item
    this.dashItem = new DashItem();
    this.dashItem.x = 5000;
    this.dashItem.y = -4000;
    this.stage.addChild(this.dashItem);

    this.on('shared', () => {
      this.ui.notificationNinimoDT = 120;
      logging(
        LOG_EVENT.COPY_SHARE,
        LOG_SCENE.RUNNING_GAME_SCENE,
        Store.instance.data.playername,
        window.navigator.userAgent
      );
    });
    logging(
      LOG_EVENT.GAME_START,
      LOG_SCENE.RUNNING_GAME_SCENE,
      Store.instance.data.playername,
      window.navigator.userAgent
    );
  }

  private gameOver(): void {
    logging(
      LOG_EVENT.GAME_OVER,
      LOG_SCENE.RUNNING_GAME_SCENE,
      Store.instance.data.playername,
      window.navigator.userAgent
    );
    this.ui.showTitle('Game Over');
    setTimeout(() => {
      this.ui.showGameOver(`2`, this.player.score.toString());
    }, 2000);
  }

  private gameClear(): void {
    logging(
      LOG_EVENT.GAME_CLEAR,
      LOG_SCENE.RUNNING_GAME_SCENE,
      Store.instance.data.playername,
      window.navigator.userAgent
    );
    this.player.dashDT = 0;
    this.player.invincibilityDT = 0;
    this.player.animatedSprite.alpha = 1;

    this.ui.showTitle('Clear!');
    this.player.trigger('CLEAR');
    this.player.update(1);

    this.isRunning = false;
    setTimeout(() => {
      const totalSeconds = Math.floor(this.duration / 1000);
      this.ui.showTitle(`${secondsFormatting(totalSeconds)}`);

      setTimeout(() => {
        sound.play('/audio/bonus.mp3', { loop: false, volume: Store.instance.state.volume as number });
        const score = secondsToScore(totalSeconds);
        this.player.score += score;
        this.ui.showTitle(`+${score} Score`);

        setTimeout(() => {
          if (this.getBread) {
            if (!Store.instance.data.costumes[2]) {
              Store.instance.data.costumes[2] = true;
              Store.instance.save();
              this.ui.showGetItemPopup(2);
              this.ui.once('POPUP_END', () => {
                if (!Store.instance.data.costumes[6]) {
                  Store.instance.data.costumes[6] = true;
                  Store.instance.save();
                  this.ui.showGetItemPopup(6);
                  this.ui.once('POPUP_END', () => {
                    Store.instance.state.hp = this.player.hp;
                    Store.instance.state.score = this.player.score;
                    this.game.changeScene(ThirdCutScene);
                  });
                } else {
                  Store.instance.state.hp = this.player.hp;
                  Store.instance.state.score = this.player.score;
                  this.game.changeScene(ThirdCutScene);
                }
              });
            }
          } else {
            if (!Store.instance.data.costumes[6]) {
              Store.instance.data.costumes[6] = true;
              Store.instance.save();
              this.ui.showGetItemPopup(6);
              this.ui.once('POPUP_END', () => {
                Store.instance.state.hp = this.player.hp;
                Store.instance.state.score = this.player.score;
                this.game.changeScene(ThirdCutScene);
              });
            } else {
              Store.instance.state.hp = this.player.hp;
              Store.instance.state.score = this.player.score;
              this.game.changeScene(ThirdCutScene);
            }
          }
        }, 2500);
      }, 2500);
    }, 2500);
  }

  public reDrawLineMap(): void {
    this.roadPolygon.clear();
    this.roadPolygon.beginTextureFill({
      texture: TextureFactory.instance.getSingleTexture('/textures/objects/etc/road.png'),
    });

    if (this.bottomFarms.length < (this.maps.length + 1) * 3) {
      for (let i = this.bottomFarms.length; i < (this.maps.length + 1) * 3; i++) {
        // Bottom Farm
        const bottomIndex = 1 + Math.floor(Math.random() * 7);
        const bottomTexture = TextureFactory.instance.getSingleTexture(`/textures/objects/etc/farm_${bottomIndex}.png`);
        const bottomFarm = new Sprite(bottomTexture);

        bottomFarm.zIndex = -1;
        bottomFarm.anchor.set(0.5, 0.01);
        bottomFarm.visible = false;

        this.bottomFarms.push(bottomFarm);
        this.mapContainer.addChild(bottomFarm);

        // Top Farm
        const upperIndex = 1 + Math.floor(Math.random() * 7);
        const topTexture = TextureFactory.instance.getSingleTexture(`/textures/objects/etc/farm_${upperIndex}.png`);
        const topFarm = new Sprite(topTexture);

        topFarm.zIndex = -1;
        topFarm.anchor.set(0.5, 0.99);
        topFarm.visible = false;

        this.upperFarms.push(topFarm);
        this.mapContainer.addChild(topFarm);
      }
    }

    if (this.fences.length < (this.maps.length + 1) * 3) {
      const texture = TextureFactory.instance.getSingleTexture('/textures/objects/etc/fence.png');

      for (let i = this.fences.length; i < (this.maps.length + 1) * 3; i++) {
        const fence = new Sprite(texture);

        fence.zIndex = 0;
        fence.anchor.set(0, 1.2);
        fence.visible = false;

        this.fences.push(fence);
        this.mapContainer.addChild(fence);
      }
    }

    if (this.grasses.length < this.maps.length + 1) {
      const texture = TextureFactory.instance.getSingleTexture('/textures/objects/etc/grass.png');

      for (let i = this.grasses.length; i < this.maps.length + 1; i++) {
        const grass = new Sprite(texture);

        grass.zIndex = 0;
        grass.anchor.set(0, 0.9);
        grass.visible = false;

        this.grasses.push(grass);
        this.mapContainer.addChild(grass);
      }
    }

    let minY = Infinity;
    let maxY = -Infinity;

    for (let i = 0; i < this.maps.length; i++) {
      const { line, range } = this.maps[i];

      this.roadPolygon.lineStyle();
      this.roadPolygon.drawPolygon([
        line[0].x,
        line[0].y - (i > 0 ? this.maps[i - 1].range : range) / 2,
        line[1].x,
        line[1].y - range / 2,
        line[1].x,
        line[1].y + range / 2,
        line[0].x,
        line[0].y + (i > 0 ? this.maps[i - 1].range : range) / 2,
      ]);

      this.roadPolygon.lineStyle({
        width: 6,
        color: 0xffc258,
      });
      this.roadPolygon.moveTo(line[0].x, line[0].y - range / 2);
      this.roadPolygon.lineTo(line[1].x, line[1].y - range / 2);
      this.roadPolygon.moveTo(line[0].x, line[0].y + range / 2);
      this.roadPolygon.lineTo(line[1].x, line[1].y + range / 2);

      if (this.drawLinePosition < line[0].x) {
        this.drawLinePosition = line[0].x;

        const grass = this.grasses.shift();
        if (grass) {
          grass.visible = true;
          grass.position.x = line[0].x;
          grass.position.y = line[0].y - range / 2;
          grass.rotation = Math.atan2(line[1].y - line[0].y, line[1].x - line[0].x);
          this.grasses.push(grass);
        }

        // bottom farms (remove & fix sprite)
        const bottomFarms = this.bottomFarms.splice(0, 3);
        for (let j = 0; j < bottomFarms.length; j++) {
          const rotation = Math.atan2(line[1].y - line[0].y, line[1].x - line[0].x);
          const length = (line[1].x - line[0].x) / 3;
          bottomFarms[j].visible = true;
          bottomFarms[j].rotation = (Math.PI / 180) * 15;
          bottomFarms[j].position.x = line[0].x + Math.cos(rotation) * (length * (j % 3));
          bottomFarms[j].position.y = line[0].y + range / 2 + Math.sin(rotation) * (230 * (j % 3));
          this.bottomFarms.push(bottomFarms[j]);
        }

        const upperFarms = this.upperFarms.splice(0, 3);
        for (let j = 0; j < upperFarms.length; j++) {
          const rotation = Math.atan2(line[1].y - line[0].y, line[1].x - line[0].x);
          const length = (line[1].x - line[0].x) / 3;
          upperFarms[j].visible = true;
          upperFarms[j].rotation = (Math.PI / 180) * 15;
          upperFarms[j].position.x = line[0].x + Math.cos(rotation) * (length * (j % 3));
          upperFarms[j].position.y = line[0].y - range / 2;
          this.upperFarms.push(upperFarms[j]);
        }

        // fences
        const fences = this.fences.splice(0, 3);
        for (let j = 0; j < fences.length; j++) {
          fences[j].visible = true;
          fences[j].rotation = Math.atan2(line[1].y - line[0].y, line[1].x - line[0].x);
          fences[j].position.x = line[0].x + Math.cos(fences[j].rotation) * (230 * (j % 3));
          fences[j].position.y = line[0].y - range / 2 + Math.sin(fences[j].rotation) * (230 * (j % 3));
          this.fences.push(fences[j]);
        }

        // Stone Obstacle
        if (this.stoneObstacle.x < this.player.position.x - 19000) {
          const rand = Math.random();
          this.stoneObstacle.x = line[0].x + (line[1].x - line[0].x) * rand;
          this.stoneObstacle.y =
            line[0].y + (line[1].y - line[0].y) * rand - range / 3 + ((Math.random() * range) / 3) * 2;
          this.stoneObstacle.zIndex = this.stoneObstacle.y;
        }

        // Monster Obstacle
        if (this.monsterObstacle.x < this.player.position.x - 28000) {
          const rand = Math.random();
          this.monsterObstacle.x = line[0].x + (line[1].x - line[0].x) * rand;
          this.monsterObstacle.y =
            line[0].y + (line[1].y - line[0].y) * rand - range / 3 + ((Math.random() * range) / 3) * 2;
          this.monsterObstacle.zIndex = this.monsterObstacle.y;
          this.monsterObstacle.change();
        }

        // Heart Item
        if (this.heartItem.x < this.player.position.x - 200000) {
          const rand = Math.random();
          this.heartItem.x = line[0].x + (line[1].x - line[0].x) * rand;
          this.heartItem.y = line[0].y + (line[1].y - line[0].y) * rand - range / 3 + ((Math.random() * range) / 3) * 2;
          this.heartItem.zIndex = Infinity;
        }

        // Heart Item
        if (this.breadItem.x < this.player.position.x - 200000) {
          const rand = Math.random();
          this.breadItem.x = line[0].x + (line[1].x - line[0].x) * rand;
          this.breadItem.y = line[0].y + (line[1].y - line[0].y) * rand - range / 3 + ((Math.random() * range) / 3) * 2;
          this.breadItem.zIndex = Infinity;
        }

        // Dash Item
        if (this.dashItem.x < this.player.position.x - 21000) {
          const rand = Math.random();
          this.dashItem.x = line[0].x + (line[1].x - line[0].x) * rand;
          this.dashItem.y = line[0].y + (line[1].y - line[0].y) * rand - range / 3 + ((Math.random() * range) / 3) * 2;
          this.dashItem.zIndex = Infinity;
        }
      }

      if (line[0].x <= this.player.position.x && line[1].x >= this.player.position.x) {
        if (line[0].y < minY) {
          minY = line[0].y;
        }
        if (line[0].y > maxY) {
          maxY = line[0].y;
        }

        if (line[1].y < minY) {
          minY = line[1].y;
        }
        if (line[1].y > maxY) {
          maxY = line[1].y;
        }
      }
    }
  }

  public updateLineMap(): void {
    this.destroyLineMap();
    this.generateLineMap();
    this.drawPlayerLine();
  }

  private drawPlayerLine(): void {}

  private destroyLineMap(): void {
    let redrawable = false;

    for (let i = this.maps.length - 1; i >= 0; i--) {
      const { line } = this.maps[i];

      if (line[1].x < this.camera.viewport[0].x) {
        this.maps.splice(i, 1);
        redrawable = true;
      }
    }

    if (redrawable) {
      this.reDrawLineMap();
    }
  }

  private generateLineMap(): void {
    const { line, range } = this.maps[this.maps.length - 1];
    let lineLength = 690;
    const shouldCreate = line[1].x < this.camera.viewport[1].x + 300 && line[1].x < this.goalPosition;
    const isLast = line[1].x + lineLength >= this.goalPosition;

    if (shouldCreate) {
      const baseRadian = Math.atan2(line[1].y - line[0].y, line[1].x - line[0].x);
      const radians = [0];
      const rotate = (Math.PI / 180) * 7;

      if (baseRadian > (-Math.PI / 180) * 20 + rotate) {
        radians.push(baseRadian - rotate);
      }
      if (baseRadian < (Math.PI / 180) * 20 - rotate) {
        radians.push(baseRadian + rotate);
      }
      const radian =
        line[1].x + lineLength >= this.goalPosition ? 0 : radians[Math.floor(Math.random() * radians.length)];

      if (isLast) {
        lineLength = 1000;
      }

      const newLine: Line = [
        { x: line[1].x, y: line[1].y },
        { x: line[1].x + Math.cos(radian) * lineLength, y: line[1].y + Math.sin(radian) * lineLength },
      ];
      this.maps.push({
        line: newLine,
        range: range,
      });

      if (line[1].x + Math.cos(radian) * lineLength >= this.goalPosition) {
        const texture = TextureFactory.instance.getSingleTexture('/textures/objects/etc/finish.png');
        const sprite = new Sprite(texture);

        sprite.x = this.goalPosition;
        sprite.y = line[1].y;
        sprite.scale.set(744 / sprite.height);
        sprite.anchor.set(1, 0.5);
        sprite.zIndex = Infinity;

        this.mapContainer.addChild(sprite);
      }

      this.reDrawLineMap();
    }
  }

  private roadRaycasting(): void {
    const points = [];
    const padding = 20;
    for (let i = 0; i < this.maps.length; i++) {
      const { line, range } = this.maps[i];
      const lines: Array<Line> = [
        [
          { x: line[0].x, y: line[0].y - (i > 0 ? this.maps[i - 1].range : range) / 2 - padding },
          { x: line[1].x, y: line[1].y - range / 2 - padding },
        ],
        [
          { x: line[0].x, y: line[0].y + (i > 0 ? this.maps[i - 1].range : range) / 2 + padding },
          { x: line[1].x, y: line[1].y + range / 2 + padding },
        ],
      ];
      const raycastLine: Line = [
        { x: this.player.position.x, y: this.player.position.y - 800 },
        { x: this.player.position.x, y: this.player.position.y + 800 },
      ];

      for (const line of lines) {
        const dt = intersectionLL(raycastLine, line);

        if (dt >= 0 && dt <= 1) {
          points.push({
            x: raycastLine[0].x + (raycastLine[1].x - raycastLine[0].x) * dt,
            y: raycastLine[0].y + (raycastLine[1].y - raycastLine[0].y) * dt,
          });
        }
      }
    }

    let diff = 0;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const point of points) {
      if (point.y < this.player.position.y) {
        diff++;
      } else {
        diff--;
      }

      if (point.y < minY) {
        minY = point.y;
      }
      if (point.y > maxY) {
        maxY = point.y;
      }
    }

    if ((points.length === 0 || diff !== 0) && this.player.state === 'WALK') {
      this.player.hit(1);
    }

    const stoneObstacleDiff = Math.sqrt(
      (this.player.position.x - this.stoneObstacle.x) ** 2 + (this.player.position.y - (this.stoneObstacle.y - 30)) ** 2
    );
    if (this.player.state === 'WALK' && stoneObstacleDiff < 60 && this.player.dashDT === 0) {
      this.player.hit(1);
    }

    const monsterObstacleDiff = Math.sqrt(
      (this.player.position.x - this.monsterObstacle.x) ** 2 +
        (this.player.position.y - this.monsterObstacle.y - 20) ** 2
    );
    if (this.player.state === 'WALK' && monsterObstacleDiff < 80 && this.player.dashDT === 0) {
      this.player.hit(1);
    }

    const heartItemDiff = Math.sqrt(
      (this.player.position.x - this.heartItem.x) ** 2 + (this.player.position.y - this.heartItem.y) ** 2
    );
    if (!this.heartItem.used && heartItemDiff < 130) {
      sound.play('/audio/getting_item.mp3', { loop: false, volume: Store.instance.state.volume as number });
      sound.play('/audio/heartget.mp3', { loop: false, volume: Store.instance.state.volume as number });
      if (this.player.hp < 5) {
        this.player.hp += 1;
      }
      this.heartItem.use();

      const healthText: VanishText = new VanishText(`HP +1`, { fill: 0x66cc66 });
      healthText.x = this.player.position.x;
      healthText.y = this.player.position.y - 90;
      healthText.on('destroy', () => {
        this.objects.splice(this.objects.indexOf(healthText), 1);
        this.stage.removeChild(healthText);
        healthText.destroy();
      });
      this.objects.push(healthText);
      this.stage.addChild(healthText);
    }

    const breadItemDiff = Math.sqrt(
      (this.player.position.x - this.breadItem.x) ** 2 + (this.player.position.y - this.breadItem.y) ** 2
    );
    if (!this.breadItem.used && breadItemDiff < 130) {
      sound.play('/audio/getting_item.mp3', { loop: false, volume: Store.instance.state.volume as number });
      this.breadItem.use();
      this.getBread = true;
    }

    const dashItemDiff = Math.sqrt(
      (this.player.position.x - this.dashItem.x) ** 2 + (this.player.position.y - this.dashItem.y) ** 2
    );

    if (!this.dashItem.used && dashItemDiff < 130) {
      sound.play('/audio/getting_item.mp3', { loop: false, volume: Store.instance.state.volume as number });
      sound.play('/audio/boost.mp3', { loop: false, volume: Store.instance.state.volume as number });
      const score = 50;

      this.player.dash(300);
      this.dashItem.use();
      this.player.score += score;

      const scoreText: VanishText = new VanishText(`+${score}`, { fill: 0xefa500 });
      scoreText.x = this.player.position.x;
      scoreText.y = this.player.position.y - 90;
      scoreText.on('destroy', () => {
        this.objects.splice(this.objects.indexOf(scoreText), 1);
        this.container.removeChild(scoreText);
        scoreText.destroy();
      });
      this.objects.push(scoreText);
      this.container.addChild(scoreText);
    }

    if (diff < 0 && points.length) {
      this.player.position.y = minY;
    } else if (diff > 0 && points.length) {
      this.player.position.y = maxY;
    }
  }

  public update(elapsed: number): void {
    super.update(elapsed);
    this.updateLineMap();

    this.breadItem.update(elapsed);
    this.heartItem.update(elapsed);
    this.dashItem.update(elapsed);

    this.ui.setHP(this.player.hp);
    this.ui.setScore(this.player.score);
    this.ui.update(elapsed);

    const goalPosition: number = this.goalPosition;
    this.progress.update(this.game.width, this.game.height, this.player.position.x / goalPosition);

    if (this.lastPosition < Math.floor(this.player.position.x / 1000)) {
      const score = this.player.dashDT > 0 ? 10 : 5;

      this.lastPosition = Math.floor(this.player.position.x / 1000);
      this.player.score += score;
    }

    if (goalPosition <= this.player.position.x) {
      if (this.player.velocity.x !== 0) {
        this.gameClear();
      }
      this.player.velocity.x = 0;
      return;
    }

    if (this.player.position.x >= 580 && this.player.targetRadian === 0) {
      if (!this.ui.showNotificationText) {
        this.ui.showNotification(LOCALE_DATA[Store.instance.state.locale].RUNNING_TUTORIAL_1);
      }
      return;
    } else if (this.ui.showNotificationText && this.showNotificationDT <= elapsed) {
      this.ui.hideNotification();
      this.showNotificationDT = 0;

      if (!this.showTapTutorial) {
        this.showTapTutorial = true;
        setTimeout(() => {
          this.ui.showNotification(LOCALE_DATA[Store.instance.state.locale].RUNNING_TUTORIAL_2);
          this.showNotificationDT = 120;
        }, 1000);
      }
    } else if (this.showNotificationDT >= elapsed) {
      this.showNotificationDT -= elapsed;
    }

    if (this.player.position.x > 580 && this.player.targetRadian !== 0 && !this.isRunning) {
      this.isRunning = true;
      this.isStarted = true;
    }

    for (const object of this.objects) {
      object.update(elapsed);
    }

    this.player.container.zIndex = this.player.position.y;

    const raycastLine: Line = [
      { x: this.player.position.x, y: this.player.position.y - 800 },
      { x: this.player.position.x, y: this.player.position.y + 800 },
    ];
    for (let i = 0; i < this.maps.length; i++) {
      const { line } = this.maps[i];
      const dt = intersectionLL(raycastLine, line);

      if (dt >= 0 && dt <= 1) {
        this.player.targetPosition.x = this.player.position.x;
        this.player.targetPosition.y = raycastLine[0].y + (raycastLine[1].y - raycastLine[0].y) * dt;

        if (this.player.dashDT > 0) {
          this.player.position.x = raycastLine[0].x + (raycastLine[1].x - raycastLine[0].x) * dt;
          this.player.position.y +=
            (raycastLine[0].y +
              (raycastLine[1].y - raycastLine[0].y) * dt -
              (this.player.position.y - this.player.velocity.y * elapsed)) *
            0.2;
          break;
        }
      }
    }

    if (this.player.state !== 'DIE') {
      if (this.player.speed < 20) {
        this.player.speed += 0.015 * elapsed;
      } else {
        this.player.speed += (20 - this.player.speed) * 0.02 * elapsed;
      }
    } else {
      this.player.speed = 0;
    }

    this.roadRaycasting();
  }

  public destroy(): void {
    super.destroy();
  }

  public static preload = async (callback: (currentValue: number, maxValue: number) => void): Promise<void> => {
    const assets: Array<string> = [
      '/audio/bgm_stage_2.mp3',
      '/audio/crash.mp3',
      '/audio/heartlose.mp3',
      '/audio/heartget.mp3',
      '/audio/getting_item.mp3',
      '/audio/moving.mp3',
      '/audio/boost.mp3',

      '/textures/etc/arrow.png',
      '/textures/objects/etc/bread.png',
      '/textures/objects/etc/grass.png',
      '/textures/objects/etc/fence.png',
      '/textures/objects/etc/farm_1.png',
      '/textures/objects/etc/farm_2.png',
      '/textures/objects/etc/farm_3.png',
      '/textures/objects/etc/farm_4.png',
      '/textures/objects/etc/farm_5.png',
      '/textures/objects/etc/farm_6.png',
      '/textures/objects/etc/farm_7.png',
      '/textures/objects/etc/road.png',
      '/textures/objects/etc/stone.png',
      '/textures/objects/etc/dash.png',
      '/textures/objects/ninimo/run.webp',
      '/textures/objects/ninimo/jump.webp',
      '/textures/objects/monster/pumpkin.webp',
      '/textures/objects/monster/watermelon.webp',
      '/textures/objects/monster/apple_1.webp',
      '/textures/objects/monster/apple_2.webp',
      '/textures/objects/monster/eggplant.webp',
      '/textures/objects/monster/cucumber.webp',
      '/textures/objects/etc/heart.png',
      '/textures/objects/etc/finish.png',
    ];

    let maxLoaded = 1;
    for (const asset of assets) {
      if (!Loader.shared.resources[asset]) {
        Loader.shared.add(asset, asset);
        maxLoaded++;
      }
    }

    let loaded: number = 1;
    return new Promise((resolve) => {
      Loader.shared.onLoad.add(() => {
        callback(++loaded, maxLoaded);
      });
      Loader.shared.onComplete.once(() => {
        resolve();
      });
      Loader.shared.load();
    });
  };

  protected pointerdown = (event: InteractionEvent) => {
    //
  };

  protected pointermove = (event: InteractionEvent) => {
    //
  };

  protected pointerup = (event: InteractionEvent) => {
    if (this.game.paused) return;
    sound.play('/audio/moving.mp3', { loop: false, volume: Store.instance.state.volume as number });
    this.player.changeDirection();
    this.player.speed += 2;
  };

  public onBegin(): void {
    if (!this.isBgmPlaying) {
      sound.play('/audio/bgm_stage_2.mp3', { loop: true, volume: Store.instance.state.volume as number });
      this.isBgmPlaying = true;
    }
  }
}

export default RunningGameScene;
